/**
 * DashboardView.js
 *
 * Desktop-only view that combines Gases, Plan, and Profile in a 3-column layout.
 * Supports resizable columns with drag handles, persisted to localStorage.
 *
 * Copyright (c) 2026 Eric Seigne <eric.seigne@cap-rel.fr>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import { GasesView } from './GasesView.js'
import { PlanView } from './PlanView.js'
import { ProfileView } from './ProfileView.js'
import { i18n } from '../i18n/index.js'
import { supportEntryKey } from '../utils/support.js'

const STORAGE_KEY = 'mvplan_dashboard_columns'
const DEFAULT_WIDTHS = [25, 42, 33] // percentages for gases, plan, profile
const MIN_WIDTH = 15 // minimum column width percentage

export class DashboardView {
  constructor(container) {
    this.container = container
    this.gasesView = null
    this.planView = null
    this.profileView = null
    this.columnWidths = this.loadColumnWidths()
    this.isDragging = false
    this.activeHandle = null
    this.startX = 0
    this.startWidths = []
    // Document-level drag handlers, installed by setupResizeHandlers()
    this.boundDrag = null
    this.boundEndDrag = null
  }

  t(key, params) {
    return i18n.t(key, params)
  }

  loadColumnWidths() {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (saved) {
        const widths = JSON.parse(saved)
        if (Array.isArray(widths) && widths.length === 3) {
          return widths
        }
      }
    } catch (e) {
      // Corrupted or unreadable storage: fall back to the default layout
      console.error(`DashboardView.loadColumnWidths: cannot read ${STORAGE_KEY}, using defaults`, e)
    }
    return [...DEFAULT_WIDTHS]
  }

  saveColumnWidths() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.columnWidths))
    } catch (e) {
      // Storage full or unavailable: the layout simply is not persisted
      console.error(`DashboardView.saveColumnWidths: cannot write ${STORAGE_KEY}`, e)
    }
  }

  render() {
    const [w1, w2, w3] = this.columnWidths

    // Create 3-column layout with flex and resize handles
    this.container.innerHTML = `
      <div class="flex flex-col h-full">
      <div class="flex flex-1 min-h-0" id="dashboard-flex">
        <!-- Left column: Gases -->
        <div class="overflow-y-auto flex-shrink-0" id="dashboard-gases" style="width: ${w1}%">
          <h2 class="text-sm font-bold mb-2 sticky top-0 bg-base-200 py-2 z-10">${this.t('nav_gases')}</h2>
          <div id="gases-container"></div>
        </div>

        <!-- Resize handle 1 -->
        <div class="resize-handle" data-handle="0"></div>

        <!-- Center column: Plan -->
        <div class="overflow-y-auto flex-shrink-0" id="dashboard-plan" style="width: ${w2}%">
          <h2 class="text-sm font-bold mb-2 sticky top-0 bg-base-200 py-2 z-10">${this.t('nav_plan')}</h2>
          <div id="plan-container"></div>
        </div>

        <!-- Resize handle 2 -->
        <div class="resize-handle" data-handle="1"></div>

        <!-- Right column: Profile -->
        <div class="overflow-y-auto flex-grow" id="dashboard-profile" style="width: ${w3}%">
          <h2 class="text-sm font-bold mb-2 sticky top-0 bg-base-200 py-2 z-10">${this.t('nav_profile')}</h2>
          <div id="profile-container"></div>
        </div>
      </div>

      <!-- Support entry: one discreet line under the three columns, never a
           popup and never a reminder that interrupts a plan being built -->
      <div class="flex-none pt-2 text-center text-xs">
        <a href="#/support" class="link link-primary">${this.t(supportEntryKey())}</a>
      </div>
      </div>
    `

    // Create sub-views in their containers
    const gasesContainer = document.getElementById('gases-container')
    const planContainer = document.getElementById('plan-container')
    const profileContainer = document.getElementById('profile-container')

    if (gasesContainer) {
      this.gasesView = new GasesView(gasesContainer)
      this.gasesView.render()
    }

    if (planContainer) {
      this.planView = new PlanView(planContainer)
      this.planView.render()
    }

    if (profileContainer) {
      this.profileView = new ProfileView(profileContainer)
      this.profileView.render()
    }

    // Setup resize handlers
    this.setupResizeHandlers()
  }

  setupResizeHandlers() {
    const handles = this.container.querySelectorAll('.resize-handle')

    handles.forEach(handle => {
      // Mouse events
      handle.addEventListener('mousedown', (e) => this.startDrag(e, handle))

      // Touch events
      handle.addEventListener('touchstart', (e) => this.startDrag(e, handle), { passive: false })
    })

    // Global mouse/touch move and up handlers
    this.boundDrag = (e) => this.drag(e)
    this.boundEndDrag = (e) => this.endDrag(e)

    document.addEventListener('mousemove', this.boundDrag)
    document.addEventListener('mouseup', this.boundEndDrag)
    document.addEventListener('touchmove', this.boundDrag, { passive: false })
    document.addEventListener('touchend', this.boundEndDrag)
  }

  destroy() {
    // Release the document-level drag listeners added by setupResizeHandlers
    if (this.boundDrag) {
      document.removeEventListener('mousemove', this.boundDrag)
      document.removeEventListener('touchmove', this.boundDrag)
      this.boundDrag = null
    }
    if (this.boundEndDrag) {
      document.removeEventListener('mouseup', this.boundEndDrag)
      document.removeEventListener('touchend', this.boundEndDrag)
      this.boundEndDrag = null
    }

    // A drag interrupted by navigation would leave the body stuck with the
    // resize cursor and text selection disabled
    if (this.isDragging) {
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      this.isDragging = false
      this.activeHandle = null
    }

    this.destroySubView('gasesView')
    this.destroySubView('planView')
    this.destroySubView('profileView')

    this.container = null
  }

  // Tear down one sub-view. Sub-views are not required to expose destroy(),
  // and a failing teardown must not skip the remaining sub-views.
  destroySubView(name) {
    const view = this[name]
    this[name] = null
    if (!view || typeof view.destroy !== 'function') return

    try {
      view.destroy()
    } catch (err) {
      console.error(`DashboardView.destroySubView: ${name} teardown failed`, err)
    }
  }

  startDrag(e, handle) {
    e.preventDefault()
    this.isDragging = true
    this.activeHandle = parseInt(handle.dataset.handle)
    this.startX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX
    this.startWidths = [...this.columnWidths]

    // Add dragging class
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }

  drag(e) {
    if (!this.isDragging) return

    e.preventDefault()

    const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX
    const flexContainer = document.getElementById('dashboard-flex')
    if (!flexContainer) return

    const containerWidth = flexContainer.offsetWidth
    const deltaX = clientX - this.startX
    const deltaPercent = (deltaX / containerWidth) * 100

    // Calculate new widths based on which handle is being dragged
    const newWidths = [...this.startWidths]

    if (this.activeHandle === 0) {
      // Dragging handle between gases and plan
      newWidths[0] = this.startWidths[0] + deltaPercent
      newWidths[1] = this.startWidths[1] - deltaPercent
    } else {
      // Dragging handle between plan and profile
      newWidths[1] = this.startWidths[1] + deltaPercent
      newWidths[2] = this.startWidths[2] - deltaPercent
    }

    // Enforce minimum widths
    if (newWidths[0] < MIN_WIDTH || newWidths[1] < MIN_WIDTH || newWidths[2] < MIN_WIDTH) {
      return
    }

    // Apply new widths
    this.columnWidths = newWidths
    this.applyColumnWidths()
  }

  applyColumnWidths() {
    const gases = document.getElementById('dashboard-gases')
    const plan = document.getElementById('dashboard-plan')
    const profile = document.getElementById('dashboard-profile')

    if (gases) gases.style.width = `${this.columnWidths[0]}%`
    if (plan) plan.style.width = `${this.columnWidths[1]}%`
    if (profile) profile.style.width = `${this.columnWidths[2]}%`
  }

  endDrag() {
    if (!this.isDragging) return

    this.isDragging = false
    this.activeHandle = null

    // Remove dragging class
    document.body.style.cursor = ''
    document.body.style.userSelect = ''

    // Save widths to localStorage
    this.saveColumnWidths()
  }
}
